import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { servicosLandingData, type ServicoLandingData } from "@/mocks/servicos-landing";

function NotFoundServico() {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#FBC52D]/15 mb-6">
        <i className="ri-emotion-sad-line text-[#FBC52D] text-4xl" />
      </div>
      <h1 className="text-3xl font-bold text-black mb-3">Serviço não encontrado</h1>
      <p className="text-gray-500 mb-8 max-w-md">
        A página que você procura não existe. Confira nossos serviços disponíveis ou volte para a página inicial.
      </p>
      <Link
        to="/"
        className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-7 py-3 rounded-full hover:bg-[#d4a800] transition-colors cursor-pointer text-sm"
      >
        Voltar para o início
      </Link>
    </div>
  );
}

export default function ServicoLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const data: ServicoLandingData | undefined = servicosLandingData.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) return <NotFoundServico />;

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      <ServicoHero data={data} />
      <OQueE data={data} />
      <Beneficios data={data} />
      <ComoFunciona data={data} />
      <Diferencial data={data} />
      <Resultados data={data} />
      <FAQ data={data} />
      <CtaSection data={data} />
      <Contato data={data} />
      <Footer />
    </div>
  );
}

function ServicoHero({ data }: { data: ServicoLandingData }) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".hero-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("hero-visible"), i * 150);
            });
          }
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-[550px] md:min-h-[650px] flex items-center">
      <img
        src={data.heroImg}
        alt={data.heroAlt}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 py-32 md:py-40">
        <div className="max-w-3xl">
          <div className="hero-item opacity-0 translate-y-6 transition-all duration-500 inline-flex items-center gap-2 mb-6">
            <span className="w-6 h-px bg-[#FBC52D]" />
            <span className="text-[#FBC52D] text-xs font-semibold tracking-widest uppercase">
              {data.tag}
            </span>
          </div>

          <h1 className="hero-item opacity-0 translate-y-6 transition-all duration-500 text-3xl md:text-4xl lg:text-6xl font-bold text-white leading-tight mb-5">
            {data.titulo}
          </h1>

          <p className="hero-item opacity-0 translate-y-6 transition-all duration-500 text-white/75 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
            {data.subtitulo}
          </p>

          <div className="hero-item opacity-0 translate-y-6 transition-all duration-500 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => document.querySelector("#landing-contato")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-7 py-3.5 rounded-full hover:bg-[#d4a800] transition-all duration-200 cursor-pointer text-sm"
            >
              Solicitar análise gratuita
            </button>
            <a
              href="https://wa.me/5511936189998"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 hover:border-white/60 transition-all duration-200 cursor-pointer text-sm"
            >
              <i className="ri-whatsapp-line text-base" />
              Falar pelo WhatsApp
            </a>
          </div>
        </div>
      </div>

      <style>{`.hero-visible{opacity:1!important;transform:translateY(0)!important;}`}</style>
    </section>
  );
}

function OQueE({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Visão geral
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight mb-6">
              {data.oQueE.titulo}
            </h2>
            {data.oQueE.paragrafos.map((p, i) => (
              <p key={i} className="text-gray-600 text-base leading-relaxed mb-4">
                {p}
              </p>
            ))}
          </div>
          <div className="relative rounded-2xl overflow-hidden h-[350px] md:h-[420px]">
            <img
              src={data.oQueE.img}
              alt={data.oQueE.titulo}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Beneficios({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-[#F8F8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-black" />
            <span className="text-black text-xs font-semibold tracking-widest uppercase">
              Benefícios
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight">
            {data.beneficios.titulo}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.beneficios.items.map((item) => (
            <div
              key={item.titulo}
              className="bg-[#FBC52D] rounded-2xl p-6 border border-transparent hover:border-black/10 hover:bg-[#e5b228] transition-all duration-300"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-black/10 mb-4">
                <i className={`${item.icone} text-black text-lg`} />
              </div>
              <h3 className="text-black font-bold text-base mb-2">{item.titulo}</h3>
              <p className="text-black/70 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComoFunciona({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-black" />
            <span className="text-black text-xs font-semibold tracking-widest uppercase">
              Processo
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight">
            {data.comoFunciona.titulo}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.comoFunciona.passos.map((passo) => (
            <div
              key={passo.numero}
              className="relative bg-[#FBC52D] rounded-2xl p-6 border border-[#FBC52D] group hover:border-black/10 transition-all duration-300"
            >
              <span className="text-5xl font-bold text-black transition-colors absolute top-4 right-5">
                {passo.numero}
              </span>
              <h3 className="text-black font-bold text-base mb-2 relative z-10">{passo.titulo}</h3>
              <p className="text-black/70 text-sm leading-relaxed relative z-10">{passo.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Diferencial({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-[#F8F8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20corporate%20strategy%20meeting%20room%20with%20warm%20golden%20lighting%2C%20professional%20team%20collaborating%2C%20sophisticated%20boardroom%20atmosphere%2C%20premium%20business%20environment%2C%20clean%20minimal%20design%2C%20architectural%20glass%20walls&width=1400&height=500&seq=landing-dif-geral&orientation=landscape"
            alt="Diferencial Ferrarezi"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/80" />
          <div className="absolute top-0 left-0 w-1 h-full bg-[#FBC52D]" />

          <div className="relative z-10 px-8 md:px-14 lg:px-20 py-16 lg:py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-6 h-px bg-[#FBC52D]" />
                <span className="text-[#FBC52D] text-xs font-semibold tracking-widest uppercase">
                  Diferencial Ferrarezi
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-6">
                {data.diferencialFerrarezi.titulo}
              </h2>
              {data.diferencialFerrarezi.paragrafos.map((p, i) => (
                <p key={i} className="text-white/70 text-base leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Resultados({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-black" />
            <span className="text-black text-xs font-semibold tracking-widest uppercase">
              Resultados
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight">
            {data.resultados.titulo}
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {data.resultados.metricas.map((m) => (
            <div
              key={m.desc}
              className="bg-[#F8F8F6] rounded-2xl p-6 text-center border border-gray-100 hover:border-[#FBC52D]/30 transition-all duration-300"
            >
              <div className="text-3xl md:text-4xl font-bold text-[#FBC52D] mb-2">{m.valor}</div>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ({ data }: { data: ServicoLandingData }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 md:py-28 bg-[#F8F8F6]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-black" />
            <span className="text-black text-xs font-semibold tracking-widest uppercase">
              Dúvidas
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight">
            Perguntas frequentes
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {data.faq.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-100 overflow-hidden cursor-pointer transition-all duration-300"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <div className="flex items-center justify-between px-6 py-5">
                <h3 className="text-black font-semibold text-sm pr-4">{item.pergunta}</h3>
                <div className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${openIndex === i ? "bg-[#FBC52D] rotate-45" : "bg-gray-100"}`}>
                  <i className={`ri-add-line text-sm ${openIndex === i ? "text-black" : "text-gray-400"}`} />
                </div>
              </div>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openIndex === i ? "max-h-96 pb-5" : "max-h-0"
                }`}
              >
                <p className="text-gray-500 text-sm leading-relaxed px-6">{item.resposta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ data }: { data: ServicoLandingData }) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20corporate%20city%20skyline%20at%20sunset%20with%20golden%20light%2C%20premium%20business%20district%2C%20sophisticated%20urban%20landscape%2C%20warm%20atmospheric%20photography%2C%20professional%20executive%20environment%2C%20dramatic%20sky&width=1400&height=500&seq=landing-cta-geral&orientation=landscape"
            alt="CTA Background"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/75" />
          <div className="absolute top-0 left-0 w-1 h-full bg-[#FBC52D]" />

          <div className="relative z-10 px-8 md:px-14 lg:px-20 py-16 lg:py-20 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4 max-w-2xl mx-auto">
              {data.ctaTitulo}
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              {data.ctaSubtitulo}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.querySelector("#landing-contato")?.scrollIntoView({ behavior: "smooth" })}
                className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-7 py-3.5 rounded-full hover:bg-[#d4a800] transition-all duration-200 cursor-pointer text-sm"
              >
                Solicitar análise gratuita
              </button>
              <a
                href="https://wa.me/5511936189998"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 hover:border-white/60 transition-all duration-200 cursor-pointer text-sm"
              >
                <i className="ri-whatsapp-line text-base" />
                Falar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contato({ data }: { data: ServicoLandingData }) {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    // Honeypot check — bot preencheu, finge sucesso
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }

    setStatus("sending");
    const formData = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (key !== "phone_alt") formData.append(key, value as string);
    });
    formData.append("servico_interesse", data.titulo);
    try {
      const res = await fetch("https://formsubmit.co/ajax/contato@ferrarezi.com.br", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="landing-contato" className="py-20 md:py-28 bg-[#F8F8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Fale conosco
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight mb-6">
              Vamos conversar sobre {data.titulo.toLowerCase()}
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">
              Preencha o formulário e um especialista da Ferrarezi entrará em contato em até 24 horas para entender suas necessidades específicas em {data.titulo.toLowerCase()}.
            </p>

            <div className="flex flex-col gap-4 mb-8">
              {[
                { icon: "ri-map-pin-line", label: "Localização", value: "São Paulo, SP, Brasil" },
                { icon: "ri-phone-line", label: "Telefone", value: "+55 11 93618-9998" },
                { icon: "ri-mail-line", label: "E-mail", value: "contato@ferrarezi.com.br" },
              ].map((info) => (
                <div key={info.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#FBC52D]/10 shrink-0">
                    <i className={`${info.icon} text-[#FBC52D] text-base`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium">{info.label}</div>
                    <div className="text-black text-sm font-medium">{info.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://wa.me/5511936189998"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-[#25D366] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#1ebe5d] transition-all duration-200 cursor-pointer text-sm"
            >
              <i className="ri-whatsapp-line text-lg" />
              Falar pelo WhatsApp
            </a>
          </div>

          <div>
            <div className="bg-white rounded-2xl p-7 md:p-9 border border-gray-100">
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FBC52D]/20 mb-5">
                    <i className="ri-check-line text-[#FBC52D] text-3xl" />
                  </div>
                  <h3 className="text-black font-bold text-xl mb-2">Mensagem enviada!</h3>
                  <p className="text-gray-500 text-sm">
                    Obrigado pelo contato. Nossa equipe retornará em até 24 horas.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="whitespace-nowrap mt-6 bg-black text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-gray-900 transition-colors cursor-pointer"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form
                  id="contato-servicos-ferrarezi"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <input type="hidden" name="servico_interesse" value={data.titulo} />
                  <input type="hidden" name="_subject" value={`Contato - ${data.titulo} | Site Ferrarezi`} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome *</label>
                      <input
                        type="text"
                        name="nome"
                        required
                        placeholder="Seu nome completo"
                        className="w-full bg-[#F8F8F6] border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Empresa *</label>
                      <input
                        type="text"
                        name="empresa"
                        required
                        placeholder="Nome da empresa"
                        className="w-full bg-[#F8F8F6] border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefone *</label>
                      <input
                        type="tel"
                        name="telefone"
                        required
                        placeholder="(11) 99999-9999"
                        className="w-full bg-[#F8F8F6] border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full bg-[#F8F8F6] border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Mensagem
                      <span className="text-gray-400 font-normal ml-1">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="mensagem"
                      rows={4}
                      maxLength={500}
                      placeholder={`Conte um pouco sobre sua empresa e seu interesse em ${data.titulo.toLowerCase()}...`}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-[#F8F8F6] border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors resize-none"
                    />
                  </div>

                  {/* Honeypot anti-spam */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute opacity-0 pointer-events-none"
                  />

                  {status === "error" && (
                    <p className="text-red-500 text-xs">Ocorreu um erro. Tente novamente ou entre em contato pelo WhatsApp.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="whitespace-nowrap w-full bg-[#FBC52D] text-black font-semibold py-3.5 rounded-xl hover:bg-[#d4a800] transition-all duration-200 cursor-pointer text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? "Enviando..." : "Solicitar contato"}
                  </button>

                  <p className="text-gray-400 text-xs text-center">
                    Seus dados estão seguros. Não compartilhamos informações com terceiros.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}